import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { ReplaySubject, Observable, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AppState, Selectors, Actions } from 'store/root-reducer';
import { BillableActionTypes } from 'store/actions/billable.actions';

import { BillableItem, UserSubscription } from 'modules/shared/models/billable.models';
import { LoadBillablePayload } from 'store/models/billable.models';

import { environment } from 'environments/environment';

interface IAppSettings {
  version: string;
  isOCROn?: boolean;
  isFaceCompareOn?: boolean;
  isNLPon?: boolean;
  usedSearchProvidersNames?: string;
}

@Component({
  selector: 'artemis-version-card',
  templateUrl: './version-card.component.html',
  styleUrls: ['./version-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VersionCardComponent implements OnInit, OnDestroy {

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  appSettings: IAppSettings = <IAppSettings>{};

  searchEngines: Array<BillableItem> = [];
  userSubscriptions: Array<UserSubscription> = [];

  loading$: Observable<boolean>;

  error$: Observable<string> = this.store.select(Selectors.billable.getError);

  constructor(private cdRef: ChangeDetectorRef,
              private store: Store<AppState>) { }

  ngOnInit() {
    this.getLoadingStatus();

    this.appSettings.version = environment.VERSION;
    this.getBillableInfo();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loading$ = this.store.select(Selectors.billable.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((billableLoading: Array<BillableActionTypes>) => {
          return billableLoading.includes(BillableActionTypes.LOAD_BILLABLE);
        })
      );
  }

  /**
   * Prepare string of inlined search providers names
   * @method prepareSearchProvidersString
   */
  private prepareSearchProvidersString(searchEngines: Array<BillableItem>): string {
    return searchEngines.filter((value: BillableItem) => value.display)
                        .map((value: BillableItem) => value.serviceName)
                        .join(', ');
  }

  /**
   * Get subscription information from state
   * @method getBillableInfo
   */
  getBillableInfo(): void {
    combineLatest([
      this.store.select(Selectors.billable.getSubscriptions),
      this.store.select(Selectors.billable.getSearchEngines),
      this.store.select(Selectors.billable.getOCR),
      this.store.select(Selectors.billable.getFC),
      this.store.select(Selectors.billable.getNLP)
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([subscriptions, searchEngines, ocrEnabled, fcEnabled, nlpEnabled]:
                  [Array<UserSubscription>, Array<BillableItem>, BillableItem, BillableItem, BillableItem]) => {
        if (subscriptions.length && searchEngines.length) {
          this.userSubscriptions = subscriptions;
          this.searchEngines = searchEngines;
          this.appSettings.isOCROn = ocrEnabled ? ocrEnabled.display : false;
          this.appSettings.isFaceCompareOn = fcEnabled ? fcEnabled.display : false;
          this.appSettings.isNLPon = nlpEnabled ? nlpEnabled.display : false;
          this.appSettings.usedSearchProvidersNames = this.prepareSearchProvidersString(searchEngines);
          this.cdRef.detectChanges();
        }
      });
  }

  /**
   * Retries get billable info again, if failed in auth guard
   * @method retryGetBillable
   */
  retryGetBillable(): void {
    this.store.dispatch(new Actions.billable.LoadBillable(<LoadBillablePayload>{ quietUpdate: false }));
  }
}
